import type { Reference, ServiceRequest } from '@medplum/fhirtypes'
import { getDateTimePeriodDisplay } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import * as appointments from '@seen/core/models/appointments/index'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { VisitNote } from '@seen/core/models/visitNote'
import { getReferenceString } from '@seen/core/references'
import { orderBy, sortBy } from 'lodash-es'
import type { JSX, ReactNode } from 'react'
import { useVersionOriginTask } from '#src/components/activity/useVersionOriginTask.ts'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import {
  ActivityComment,
  ActivityEntry,
  ActivityList,
  Link,
  TextLink,
} from '#src/components/index.ts'
import {
  useEncounterActivity,
  useRead,
  useResourceVersions,
  useSearch,
  useSearchOne,
} from '#src/hooks/index.ts'
import { useVisitNoteHref } from '#src/hooks/useVisitNoteHref.ts'

type AppointmentActivityItem = {
  action: ReactNode
  appointment: appointments.ExtendedAppointment
  key?: string
}

type VisitNoteActivityItem = {
  action: ReactNode
  visitNote: VisitNote
  key?: string
}

export function AppointmentActivity({
  appointment,
}: {
  appointment: appointments.ExtendedAppointment
}): JSX.Element {
  const { data: pastAppointments = [] } = useResourceVersions(
    appointments.ExtendedAppointment,
    appointment.id
  )
  const sortedAppointments = sortBy(pastAppointments, (a) => a.lastUpdated)
  const appointmentOriginOf = useVersionOriginTask(sortedAppointments)

  const encounterActivity = useEncounterActivity([appointment])

  // Get visit note for this appointment
  const { data: visitNote } = useSearchOne(
    VisitNote,
    VisitNote.byAppointment(appointment.reference)
  )

  const appointmentActivity: AppointmentActivityItem[] =
    sortedAppointments.reduce<AppointmentActivityItem[]>((acc, curr) => {
      if (acc.length === 0) {
        return [
          {
            action: `appointment created${curr.elationId ? ' from Elation' : ''}`,
            appointment: curr,
            key: `${curr.versionId}-created`,
          },
        ]
      }

      const prev = acc[acc.length - 1]
      const changes: ReactNode[] = []

      if (curr.status !== prev.appointment.status) {
        if (curr.status === 'cancelled') {
          acc.push({
            action: 'appointment cancelled',
            appointment: curr,
            key: `${curr.versionId}-cancelled`,
          })
          return acc
        }
      }

      const prevStart = prev.appointment.start
      const currStart = curr.start
      const prevEnd = prev.appointment.end
      const currEnd = curr.end

      if (prevStart !== currStart || prevEnd !== currEnd) {
        changes.push(
          <span>
            rescheduled{' '}
            <span className="font-medium">
              {getDateTimePeriodDisplay({
                start: prevStart,
                end: prevEnd,
              })}
            </span>
            {' → '}
            <span className="font-medium">
              {getDateTimePeriodDisplay({
                start: currStart,
                end: currEnd,
              })}
            </span>
          </span>
        )
      }

      const prevOrderRefs = prev.appointment.basedOn ?? []
      const currOrderRefs = curr.basedOn ?? []

      const prevOrderRefStrings = prevOrderRefs.map(getReferenceString)
      const currOrderRefStrings = currOrderRefs.map(getReferenceString)

      const addedOrderRefs = currOrderRefs.filter(
        (r) => !prevOrderRefStrings.includes(getReferenceString(r))
      )
      const removedOrderRefs = prevOrderRefs.filter(
        (r) => !currOrderRefStrings.includes(getReferenceString(r))
      )

      for (const orderRef of addedOrderRefs) {
        changes.push(
          <span key={`added-${getReferenceString(orderRef)}`}>
            order linked <OrderLink orderRef={orderRef} />
          </span>
        )
      }

      for (const orderRef of removedOrderRefs) {
        changes.push(
          <span key={`removed-${getReferenceString(orderRef)}`}>
            order unlinked <OrderLink orderRef={orderRef} />
          </span>
        )
      }

      const prevLocationRefString =
        prev.appointment.location &&
        getReferenceString(prev.appointment.location)
      const currLocationRefString =
        curr.location && getReferenceString(curr.location)
      if (prevLocationRefString !== currLocationRefString) {
        changes.push(
          <span>
            changed location{' '}
            <span className="font-medium">
              {referenceDisplay(prev.appointment.location)} →{' '}
              {referenceDisplay(curr.location)}
            </span>
          </span>
        )
      }

      const prevTransport = prev.appointment.requiresTransportationBoolean
      const currTransport = curr.requiresTransportationBoolean
      if (prevTransport !== currTransport) {
        changes.push(
          <span>
            transportation {currTransport ? 'required' : 'not required'}
          </span>
        )
      }

      const prevPractitionerRefString =
        prev.appointment.practitioners?.[0] &&
        getReferenceString(prev.appointment.practitioners?.[0])
      const currPractitionerRefString =
        curr.practitioners?.[0] && getReferenceString(curr.practitioners?.[0])
      if (prevPractitionerRefString !== currPractitionerRefString) {
        changes.push(
          <span>
            changed practitioner{' '}
            <span className="font-medium">
              {referenceDisplay(prev.appointment.practitioners?.[0])} →{' '}
              {referenceDisplay(curr.practitioners?.[0])}
            </span>
          </span>
        )
      }

      if (changes.length > 0) {
        acc.push({
          action: (
            <span className="inline-flex flex-col gap-0.5">
              {changes.map((c, i) => (
                <span key={i}>{c}</span>
              ))}
            </span>
          ),
          appointment: curr,
          key: `${curr.versionId}-changes`,
        })
        return acc
      }

      return acc
    }, [])

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([appointment.reference])
  )

  const visitNoteHref = useVisitNoteHref(visitNote)

  // Create visit note activity entry if it exists
  const visitNoteActivity: VisitNoteActivityItem[] =
    visitNote && visitNoteHref
      ? [
          {
            action: (
              <span>
                created a <TextLink to={visitNoteHref}>visit note</TextLink>
              </span>
            ),
            visitNote,
            key: `visit-note-${visitNote.id}`,
          },
        ]
      : []

  const all = orderBy(
    [
      ...appointmentActivity,
      ...encounterActivity,
      ...visitNoteActivity,
      ...comments,
    ],
    (a) => {
      if (a instanceof CommentCommunication) {
        return a.sent
      }
      if ('encounter' in a) {
        return a.encounter.lastUpdated
      }
      if ('visitNote' in a) {
        return a.visitNote.date ?? a.visitNote.lastUpdated
      }
      if ('appointment' in a) {
        return a.appointment.lastUpdated
      }
      return new Date(0).toISOString() // fallback for unknown types
    },
    ['desc']
  )

  return (
    <ActivityList>
      {all.map((entry) => {
        if (entry instanceof CommentCommunication) {
          return (
            <ActivityComment key={entry.reference.reference} comment={entry} />
          )
        }
        if ('encounter' in entry) {
          return (
            <ActivityEntry
              key={entry.key ?? entry.encounter.versionId}
              author={entry.encounter.lastUpdatedBy}
              time={entry.encounter.lastUpdated}
              action={entry.action}
            />
          )
        }
        if ('visitNote' in entry) {
          const author =
            entry.visitNote.signedBy ?? entry.visitNote.practitioner
          if (!author) {
            return null
          }

          return (
            <ActivityEntry
              key={entry.key ?? entry.visitNote.id}
              author={author}
              time={entry.visitNote.date ?? entry.visitNote.lastUpdated}
              action={entry.action}
            />
          )
        }
        if ('appointment' in entry) {
          return (
            <ActivityEntry
              key={entry.key ?? entry.appointment.versionId}
              author={entry.appointment.lastUpdatedBy}
              time={entry.appointment.lastUpdated}
              action={entry.action}
              originTask={appointmentOriginOf(entry.appointment)}
            />
          )
        }
        return null // fallback for unknown types
      })}
    </ActivityList>
  )
}

function OrderLink({
  orderRef,
}: {
  orderRef: Reference<ServiceRequest>
}): ReactNode {
  const { data: order } = useRead(Order, orderRef)
  const { data: participant } = useRead(Participant, order?.participant)

  if (!order) {
    return null
  }

  return (
    <Link
      to={`/Patient/${participant?.id}/orders/${order.id}`}
      className="inline-flex items-center gap-1 text-foreground hover:underline"
      tabIndex={-1}
    >
      <IconOrderStatus
        status={order.status}
        isSubmitting={order?.isSubmitting}
      />
      <span className="line-clamp-1 max-w-[200px] break-all text-ellipsis">
        {order.titleWithId}
      </span>
    </Link>
  )
}
